import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import PageSEO from "@/components/PageSEO";
import { Star, ShieldCheck, Award, Users, Waves } from "lucide-react";
import SurfBookingForm from "@/components/surf/SurfBookingForm";
import SurfWaveWhatsApp from "@/components/surf/SurfWaveWhatsApp";
import SurfCourseCard, { type SurfCourse } from "@/components/surf/SurfCourseCard";
import SurfSectionSlider, { type SurfSection } from "@/components/surf/SurfSectionSlider";
import SurfCtaSection from "@/components/surf/SurfCtaSection";
import surfKiteHero from "@/assets/surf-kite-hero.jpg.asset.json";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { T } from "@/hooks/useAITranslate";

const PAGE_PATH = "/maroc-surfing";

const FAQ: { q: string; a: string }[] = [
  {
    q: "Quelle est la meilleure période pour le surf à Essaouira ?",
    a: "Essaouira offre des vagues toute l'année grâce aux alizés atlantiques. La saison idéale pour débuter va d'avril à octobre (eau plus douce, houle régulière). Pour les surfeurs confirmés, novembre à mars concentre les meilleures houles longues — c'est aussi la haute saison pour un véritable surf trip Maroc.",
  },
  {
    q: "Essaouira est-il le meilleur spot de surf au Maroc ?",
    a: "Essaouira est l'un des meilleurs spots de surf du Maroc pour débuter : baie protégée, vagues régulières, beach-breaks pour tous niveaux et une école de surf à Essaouira présente sur la plage. Pour les surfeurs avancés, Taghazout et Imsouane complètent la côte atlantique avec des point-breaks mondialement reconnus.",
  },
  {
    q: "Quelle différence entre surf, kitesurf et windsurf ?",
    a: "Le surf utilise uniquement la vague et une planche. Le kitesurf ajoute une aile (kite) tractée par le vent — c'est la discipline reine d'Essaouira grâce aux alizés. Le windsurf combine une planche et une voile fixée à un mât. Apprendre le kitesurf à Essaouira est particulièrement adapté grâce au plan d'eau plat de la baie.",
  },
  {
    q: "Quel niveau faut-il pour débuter le surf ou le kitesurf ?",
    a: "Aucun niveau préalable n'est requis pour nos cours de surf à Essaouira : il suffit d'être à l'aise dans l'eau et de savoir nager 25 m. Pour un cours de kitesurf pour débutants, comptez 6 à 9 heures d'apprentissage progressif (théorie, pilotage du kite à terre, water-start).",
  },
  {
    q: "Quelle est la température de l'eau à Essaouira ?",
    a: "L'eau oscille entre 17 °C en hiver et 21 °C en été. Une combinaison 3/2 mm convient d'avril à octobre, une 4/3 mm est recommandée en hiver. Nous fournissons la combinaison adaptée à votre stage de surf à Essaouira.",
  },
  {
    q: "Le matériel (planche, combinaison, kite) est-il fourni ?",
    a: "Oui. La location de matériel est incluse dans tous nos cours et stages : planche en mousse pour débuter, planche dure pour confirmés, combinaison néoprène, leash et lycra. Pour le kitesurf, ailes (4 à 12 m²), planches twin-tip, harnais et casque sont également inclus.",
  },
  {
    q: "Combien coûte un cours de surf à Essaouira ?",
    a: "Un cours de surf à Essaouira débute autour de 30 à 45 € pour 2 h en groupe (matériel inclus). Un stage de surf à Essaouira de 5 jours revient à 150–220 € selon la formule. Le kitesurf, plus technique, démarre vers 70 €/h en cours particulier.",
  },
  {
    q: "Qu'est-ce qu'un surf camp au Maroc et est-ce inclus ?",
    a: "Un surf camp Maroc combine hébergement, cours quotidiens, repas et transferts. Nous travaillons avec des hébergements partenaires à Essaouira et Taghazout : indiquez « surf camp » dans le formulaire de réservation et nous construisons un séjour sur mesure incluant un surf trip Maroc multi-spots si vous le souhaitez.",
  },
  {
    q: "Le transport depuis mon hôtel ou l'aéroport est-il inclus ?",
    a: "Cochez « Besoin de transport » dans le formulaire : Maroc Transfers organise un transfert privé aller-retour depuis votre hôtel, votre riad ou l'aéroport de Marrakech-Ménara vers le spot, en véhicule climatisé avec chauffeur professionnel.",
  },
  {
    q: "Faut-il une assurance pour pratiquer le surf au Maroc ?",
    a: "Nos écoles partenaires sont assurées en responsabilité civile et tous les moniteurs sont diplômés (BPJEPS, ISA ou équivalent marocain). Nous recommandons néanmoins une assurance voyage personnelle couvrant les sports nautiques pour les longs séjours.",
  },
  {
    q: "Peut-on apprendre le surf en quelques jours ?",
    a: "Oui : la plupart des élèves se lèvent debout sur la planche dès le premier cours grâce aux vagues longues et progressives d'Essaouira. Un stage de 3 à 5 jours permet d'enchaîner les vagues en autonomie — l'un des meilleurs spots de surf du Maroc pour progresser vite.",
  },
  {
    q: "Quel âge minimum pour les cours de surf et kitesurf ?",
    a: "Les cours de surf à Essaouira accueillent les enfants dès 6 ans (cours kids dédié). Pour le kitesurf, l'âge minimum est de 12 ans avec accord parental, en raison de la puissance de l'aile.",
  },
];

const MarocSurfing = () => {
  const [courses, setCourses] = useState<SurfCourse[]>([]);
  const [loading, setLoading] = useState(true);
  const [sections, setSections] = useState<Record<string, SurfSection & { button_label: string; button_target: string }>>({});

  useEffect(() => {
    supabase
      .from("surf_page_sections")
      .select("section_key, title, description, image_urls, alt_keyword, button_label, button_target")
      .order("position", { ascending: true })
      .then(({ data }) => {
        const map: Record<string, any> = {};
        (data || []).forEach((r: any) => { map[r.section_key] = r; });
        setSections(map);
      });
  }, []);

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase
        .from("surf_courses")
        .select(
          "id, slug, title, category, description, duration, price, price_2h, price_4h, price_6h, price_private, price_semi_private, price_group, price_private_1h, price_private_4h, price_private_6h, price_semi_private_1h, price_semi_private_4h, price_semi_private_6h, price_group_1h, price_group_4h, price_group_6h, currency, rating, reviews_count, manual_reviews_count, image_url, image_urls"
        )
        .eq("is_published", true)
        .order("slot", { ascending: true });
      setCourses((data as SurfCourse[]) || []);
      setLoading(false);
    };
    load();

    const channel = supabase
      .channel("surf_courses_stats")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "surf_courses" },
        () => load()
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "surf_reviews" },
        () => load()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const avgRating =
    courses.length > 0
      ? (
          courses.reduce((s, c) => s + Number(c.rating || 0), 0) / courses.length
        ).toFixed(1)
      : "5.0";
  const totalReviews = courses.reduce(
    (s, c) => s + (c.reviews_count || 0) + (c.manual_reviews_count || 0),
    0,
  );

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "TouristAttraction",
    name: "Maroc Surfing — Cours de surf au Maroc",
    description:
      "Apprendre le surf au Maroc : cours de surf, kite-surf, wind-surf et trip-surf à Essaouira, Taghazout et le long de la côte atlantique.",
    url: "https://maroctransfers.com/maroc-surfing",
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: avgRating,
      reviewCount: Math.max(totalReviews, 1),
    },
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="surf-theme min-h-screen bg-[#f7f9fb] text-[#1a2a36] antialiased">
      <PageSEO
        title="Cours de surf Essaouira, kitesurf & surf camp Maroc"
        description="École de surf Essaouira : cours de surf, kitesurf, stage et surf camp Maroc. Moniteurs certifiés, transport inclus, réservation WhatsApp."
        path={PAGE_PATH}
      />
      <script type="application/ld+json" suppressHydrationWarning>
        {JSON.stringify(jsonLd)}
      </script>
      <script type="application/ld+json" suppressHydrationWarning>
        {JSON.stringify(faqJsonLd)}
      </script>

      {/* Top bar */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <a href="/maroc-surfing" className="flex items-center gap-2 font-bold text-primary">
            <Waves className="w-6 h-6 text-primary" />
            <span className="text-lg">Maroc Surfing</span>
          </a>
          <nav className="hidden sm:flex items-center gap-6 text-sm text-gray-700">
            <a href="#courses" className="hover:text-primary"><T>Cours</T></a>
            <a href="#reviews" className="hover:text-primary"><T>Avis</T></a>
            <a href="#booking" className="hover:text-primary"><T>Réserver</T></a>
          </nav>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <a
              href="https://maroctransfers.com"
              className="hidden md:inline text-xs sm:text-sm text-gray-500 hover:text-primary underline-offset-2 hover:underline"
              rel="noopener"
            >
              Maroc Transfers ↗
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative bg-gradient-to-b from-[#eaf5f1] to-[#f7f9fb] border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 md:py-16">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-white border border-gray-200 px-3 py-1 text-xs font-semibold text-primary mb-4">
                <Award className="w-3.5 h-3.5" />
                <T>Note moyenne</T> {avgRating}/5 · {totalReviews} <T>avis vérifiés</T>
              </div>
              <h1 className="text-3xl md:text-5xl font-extrabold text-primary leading-tight">
                Maroc Surfing — École de surf & kitesurf à Essaouira
              </h1>
              <p className="text-lg md:text-xl text-primary/80 mt-3">
                <T>École de surf Essaouira : cours de surf, stage de surf Essaouira, cours de kitesurf pour débutants et surf camp Maroc, avec moniteurs certifiés et transport organisé.</T>
              </p>
              <p className="text-sm text-gray-600 mt-4 max-w-prose">
                <T>Considérée comme le meilleur spot de surf du Maroc pour progresser, Essaouira combine vagues régulières et alizés idéaux. Surf, kitesurf, windsurf, trip-surf : choisissez votre activité, votre date et profitez d'un transfert aller-retour optionnel depuis votre hôtel grâce à notre partenaire</T>{" "}
                <a
                  href="https://maroctransfers.com"
                  className="text-primary font-semibold hover:underline"
                  rel="noopener"
                >
                  Maroc Transfers
                </a>
                .
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  href="#booking"
                  className="inline-flex items-center justify-center rounded-md bg-primary hover:bg-primary/90 text-white font-semibold px-6 py-3 transition-colors"
                >
                  <T>Réserver maintenant</T>
                </a>
                <a
                  href="#courses"
                  className="inline-flex items-center justify-center rounded-md bg-white border border-gray-300 hover:border-primary text-primary font-semibold px-6 py-3 transition-colors"
                >
                  <T>Voir les cours</T>
                </a>
              </div>
              <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-gray-600">
                <span className="inline-flex items-center gap-1">
                  <ShieldCheck className="w-4 h-4 text-primary" /> <T>Moniteurs diplômés</T>
                </span>
                <span className="inline-flex items-center gap-1">
                  <Users className="w-4 h-4 text-primary" /> {Math.max(totalReviews, 250)}+ <T>surfeurs accueillis</T>
                </span>
                <span className="inline-flex items-center gap-1">
                  <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" /> <T>Avis vérifiés</T>
                </span>
              </div>
            </div>
            <div className="hidden md:block">
              <div className="aspect-[4/3] rounded-2xl shadow-xl ring-1 ring-black/5 relative overflow-hidden">
                <img
                  src={surfKiteHero.url}
                  alt="Kitesurf en action sur la côte atlantique marocaine — Maroc Surfing"
                  width={800}
                  height={600}
                  fetchPriority="high"
                  loading="eager"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 text-white">
                  <p className="text-sm opacity-90"><T>Spot du jour</T></p>
                  <p className="text-2xl font-bold drop-shadow"><T>Essaouira, Maroc</T></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Courses */}
      <section id="courses" className="py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl md:text-3xl font-bold text-primary">
            <T>Cours de surf Essaouira, kitesurf et surf camp Maroc</T>
          </h2>
          <p className="text-gray-600 mt-2 max-w-2xl">
            <T>Apprendre le surf au Maroc avec une équipe locale : initiation, stage de surf Essaouira, cours de kitesurf pour débutants, windsurf et surf trip Maroc le long de la côte atlantique — l'un des meilleurs spots de surf du Maroc.</T>
          </p>

          {loading ? (
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-72 rounded-xl bg-white border border-gray-200 animate-pulse" />
              ))}
            </div>
          ) : courses.length === 0 ? (
            <div className="mt-8 rounded-xl border border-dashed border-gray-300 bg-white p-10 text-center">
              <p className="text-gray-600">
                <T>Catalogue en cours de préparation. Réservez directement via le formulaire ci-dessous — nous répondons sous 1 h sur WhatsApp.</T>
              </p>
            </div>
          ) : (
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {courses.map((c) => (
                <SurfCourseCard key={c.id} course={c} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Reviews / trust */}
      <section id="reviews" className="py-12 md:py-16 bg-white border-y border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl md:text-3xl font-bold text-primary">
            <T>Avis clients</T>
          </h2>
          <p className="text-gray-600 mt-2">
            <T>Note moyenne</T> <span className="font-bold text-primary">{avgRating}/5</span> · {totalReviews} <T>avis vérifiés</T>
          </p>
          <div className="mt-6 grid md:grid-cols-3 gap-5">
            {[
              { name: "Camille (FR)", text: "Cours nickel, prof patient et bonne humeur. Le transport depuis Marrakech était parfait." },
              { name: "Liam (UK)", text: "Best surf experience in Morocco. Highly recommend the combo with airport transfer." },
              { name: "Sofia (ES)", text: "Material en perfecto estado, monitor muy profesional. Repetiremos!" },
            ].map((r) => (
              <div key={r.name} className="rounded-xl border border-gray-200 p-5">
                <div className="flex items-center gap-1 mb-2">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  ))}
                </div>
                <p className="text-sm text-gray-700">« {r.text} »</p>
                <p className="text-xs text-gray-500 mt-3 font-semibold">{r.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking form */}
      <section id="booking" className="py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl md:text-3xl font-bold text-primary">
            <T>Réserver votre session de surf</T>
          </h2>
          <p className="text-gray-600 mt-2">
            <T>Remplissez les champs (tous facultatifs) et envoyez : votre demande arrive sur notre WhatsApp et est enregistrée dans notre suivi commercial.</T>
          </p>
          <div className="mt-6">
            <SurfBookingForm />
          </div>
        </div>
      </section>

      {/* SEO sections: Surf / Kitesurf / Windsurf-Wingfoil */}
      {sections.surf_essaouira && <SurfSectionSlider section={sections.surf_essaouira} />}
      {sections.kitesurf && <SurfSectionSlider section={sections.kitesurf} reverse />}
      {sections.windsurf_wingfoil && <SurfSectionSlider section={sections.windsurf_wingfoil} />}

      {/* CTA: Groupes & Débutants */}
      {sections.cta_groups && (
        <SurfCtaSection
          title={sections.cta_groups.title}
          description={sections.cta_groups.description}
          buttonLabel={sections.cta_groups.button_label}
          buttonTarget={sections.cta_groups.button_target || "#booking"}
        />
      )}

      {/* FAQ */}
      <section className="py-12 md:py-16 bg-white border-t border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl md:text-3xl font-bold text-primary">
            <T>Questions fréquentes</T>
          </h2>
          <div className="mt-6 space-y-4">
            {FAQ.map((item) => (
              <details key={item.q} className="rounded-xl border border-gray-200 p-4 group">
                <summary className="cursor-pointer font-semibold text-primary">
                  <h3 className="inline"><T>{item.q}</T></h3>
                </summary>
                <p className="text-gray-700 text-sm mt-2"><T>{item.a}</T></p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-primary text-white/80 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <p>© {new Date().getFullYear()} Maroc Surfing — opéré avec{" "}
            <a href="https://maroctransfers.com" className="text-primary hover:underline" rel="noopener">
              Maroc Transfers
            </a>.
          </p>
          <p className="text-xs text-white/90">
            Transferts, road-trips et activités au Maroc.
          </p>
        </div>
      </footer>

      <SurfWaveWhatsApp />
    </div>
  );
};

export default MarocSurfing;